import React from 'react';
import { Link } from 'react-router-dom';
import {
  Layers, Cog, Zap, CheckCircle2, ShieldCheck, BadgeDollarSign, Headphones,
  ArrowRight,
  Car, Lightbulb, Stethoscope, Gem, Building2, GraduationCap, HeartPulse, Bot,
  X as XIcon
} from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const industryIcons = [Car, Lightbulb, Stethoscope, Gem, Building2, GraduationCap, HeartPulse, Bot];

const Services = () => {
  const { t } = useLang();

  const why = [
    { icon: Zap, title: t.services.why1Title, desc: t.services.why1Desc },
    { icon: ShieldCheck, title: t.services.why2Title, desc: t.services.why2Desc },
    { icon: BadgeDollarSign, title: t.services.why3Title, desc: t.services.why3Desc },
    { icon: Headphones, title: t.services.why4Title, desc: t.services.why4Desc }
  ];

  const techBlocks = [
    {
      tag: 'SLA',
      icon: Cog,
      heading: t.services.slaHeading,
      long: t.services.slaLong,
      features: t.services.slaFeatures,
      img: 'https://images.pexels.com/photos/12268465/pexels-photo-12268465.jpeg',
      alt: 'SLA'
    },
    {
      tag: 'SLS',
      icon: Zap,
      heading: t.services.slsHeading,
      long: t.services.slsLong,
      features: t.services.slsFeatures,
      img: 'https://images.pexels.com/photos/19124461/pexels-photo-19124461.jpeg',
      alt: 'SLS'
    },
    {
      tag: 'FDM',
      icon: Layers,
      heading: t.services.fdmHeading,
      long: t.services.fdmLong,
      features: t.services.fdmFeatures,
      img: 'https://images.pexels.com/photos/17509941/pexels-photo-17509941.jpeg',
      alt: 'FDM'
    }
  ];

  // Helper to render Yes / No cells in comparison table
  const renderCell = (v) => {
    if (v === 'Evet' || v === 'Yes') {
      return <span className="inline-flex items-center gap-1 text-emerald-600 font-medium"><CheckCircle2 className="w-4 h-4" /> {v}</span>;
    }
    if (v === 'Hayır' || v === 'No') {
      return <span className="inline-flex items-center gap-1 text-zinc-400 font-medium"><XIcon className="w-4 h-4" /> {v}</span>;
    }
    return <span className="text-zinc-700">{v}</span>;
  };

  return (
    <div className="bg-white">
      <section className="relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-[420px] h-[420px] rounded-full bg-orange-500/10 blur-3xl" />
        <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-10 relative">
          <h1 className="text-4xl md:text-5xl font-bold text-zinc-900">{t.services.title}</h1>
          <p className="mt-3 text-zinc-600 max-w-2xl">{t.services.subtitle}</p>
        </div>
      </section>

      {techBlocks.map((tech, idx) => {
        const imageOnRight = idx % 2 === 0;
        const Icon = tech.icon;
        return (
          <section
            key={tech.tag}
            className={`py-12 ${idx % 2 === 1 ? 'bg-zinc-50 border-y border-zinc-200' : ''}`}
          >
            <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
              <div className={imageOnRight ? 'order-2 lg:order-1' : ''}>
                <div className="inline-flex items-center gap-2 text-orange-600 text-xs uppercase tracking-widest mb-3">
                  <Icon className="w-3.5 h-3.5" /> {tech.tag}
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-zinc-900">{tech.heading}</h2>
                <p className="mt-4 text-zinc-600 leading-relaxed">{tech.long}</p>
                <ul className="mt-6 space-y-2">
                  {tech.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-zinc-700">
                      <CheckCircle2 className="w-5 h-5 text-orange-500 shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/quote">
                  <Button className="mt-6 bg-orange-500 hover:bg-orange-600 text-white font-semibold border-0">
                    {t.nav.getQuote} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
              <div className={`relative rounded-2xl overflow-hidden border border-zinc-200 shadow-sm ${imageOnRight ? 'order-1 lg:order-2' : ''}`}>
                <img src={tech.img} alt={tech.alt} className="w-full aspect-[4/3] object-cover" />
              </div>
            </div>
          </section>
        );
      })}

      {/* COMPARISON TABLE */}
      <section className="py-20 border-t border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900">{t.services.compareTitle}</h2>
            <p className="mt-3 text-zinc-600">{t.services.compareSubtitle}</p>
          </div>

          <div className="overflow-x-auto rounded-xl border border-zinc-200 shadow-sm">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  {t.services.compareCols.map((c, i) => (
                    <th
                      key={i}
                      className={`text-left px-5 py-4 font-semibold ${
                        i === 0 ? 'text-zinc-500 uppercase text-xs tracking-wider w-1/4' : 'text-zinc-900'
                      }`}
                    >
                      {i === 0 ? c : (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-orange-500" />
                          {c}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {t.services.compareRows.map((row, i) => (
                  <tr key={i} className={`border-b border-zinc-100 last:border-0 ${i % 2 === 0 ? 'bg-white' : 'bg-zinc-50/40'} hover:bg-orange-50/40 transition-colors`}>
                    <td className="px-5 py-4 text-zinc-500 font-medium">{row[0]}</td>
                    <td className="px-5 py-4">{renderCell(row[1])}</td>
                    <td className="px-5 py-4">{renderCell(row[2])}</td>
                    <td className="px-5 py-4">{renderCell(row[3])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-20 border-t border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900">{t.services.industriesTitle}</h2>
            <p className="mt-3 text-zinc-600">{t.services.industriesSubtitle}</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {t.services.industries.map((ind, i) => {
              const Icon = industryIcons[i % industryIcons.length];
              return (
                <div key={i} className="group bg-white border border-zinc-200 rounded-xl p-5 hover:border-orange-400 hover:shadow-md transition-all">
                  <Icon className="w-7 h-7 text-orange-500 mb-3 group-hover:scale-110 transition-transform" />
                  <div className="text-zinc-900 font-semibold">{ind.t}</div>
                  <div className="text-zinc-500 text-xs mt-1.5 leading-relaxed">{ind.d}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-20 border-t border-zinc-200 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 text-center">{t.services.whyTitle}</h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {why.map((w, i) => (
              <Card key={i} className="bg-white border-zinc-200 hover:border-orange-400 hover:shadow-md transition-all p-6">
                <div className="w-11 h-11 rounded-lg bg-orange-500/10 border border-orange-500/20 flex items-center justify-center mb-4">
                  <w.icon className="w-5 h-5 text-orange-500" />
                </div>
                <h3 className="text-zinc-900 font-semibold">{w.title}</h3>
                <p className="text-zinc-600 text-sm mt-2 leading-relaxed">{w.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 border-t border-zinc-200 bg-white">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900">{t.services.faqTitle}</h2>
            <p className="mt-3 text-zinc-600">{t.services.faqSubtitle}</p>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {t.services.faqs.map((faq, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-zinc-200">
                <AccordionTrigger className="text-left text-zinc-900 font-medium hover:text-orange-600 hover:no-underline py-4">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-zinc-600 leading-relaxed pb-4">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default Services;
